import type { ApiEnvelope, RequestStats } from './types';

interface RequestOptions {
  signal?: AbortSignal;
  priority?: 'visible' | 'offscreen';
  retry503?: boolean;
  reuseCompleted?: boolean;
}

interface Consumer<T> {
  resolve(value: T): void;
  reject(reason: unknown): void;
  signal?: AbortSignal;
  abort?: () => void;
}

interface PendingTask<T = unknown> {
  key: string;
  url: string;
  priority: number;
  controller: AbortController;
  consumers: Set<Consumer<T>>;
  started: boolean;
  retry503: boolean;
  reuseCompleted: boolean;
}

interface RetainedResponse {
  value: unknown;
  bytes: number;
}

export type RequestActivityListener = (pendingRequests: number) => void;

export class HttpError extends Error {
  constructor(public status: number, message: string, public retryAfterSeconds: number | null = null) {
    super(message);
    this.name = 'HttpError';
  }
}

export class StaleResponseError extends Error {
  constructor() {
    super('A newer request has replaced this response');
    this.name = 'StaleResponseError';
  }
}

export function canonicalRequestUrl(input: string): string {
  const [path, query = ''] = input.split('?', 2);
  const normalizedPath = path.replace(/\/{2,}/g, '/');
  if (!query) return normalizedPath;
  const params = new URLSearchParams(query);
  params.sort();
  return `${normalizedPath}?${params.toString()}`;
}

/** Per-browser backpressure: keep two workers available without allowing a four-request browser burst. */
export const PRODUCTION_REQUEST_CONCURRENCY = 2;

export class RequestManager {
  private active = 0;
  private readonly queued: PendingTask[] = [];
  private readonly inflight = new Map<string, PendingTask>();
  private readonly retained = new Map<string, RetainedResponse>();
  private retainedBytes = 0;
  private readonly generations = new Map<string, number>();
  private readonly scopeControllers = new Map<string, AbortController>();
  private readonly activityListeners = new Set<RequestActivityListener>();
  private publishedPendingRequests = 0;
  private _stats: RequestStats = { queued: 0, active: 0, coalesced: 0, reused: 0, completed: 0, retried: 0, abandoned: 0 };

  constructor(
    private concurrency = PRODUCTION_REQUEST_CONCURRENCY,
    private readonly fetcher: typeof fetch = fetch,
    private readonly retainedLimitBytes = 8 * 1024 * 1024
  ) {
    this.setConcurrency(concurrency);
  }

  setConcurrency(value: number): void {
    if (![1, 2, 4].includes(value)) throw new RangeError('Browser request concurrency must be 1, 2, or 4');
    this.concurrency = value;
    this.pump();
  }

  get stats(): Readonly<RequestStats> {
    return { ...this._stats, queued: this.queued.length, active: this.active };
  }

  subscribeActivity(listener: RequestActivityListener): () => void {
    this.activityListeners.add(listener);
    listener(this.active + this.queued.length);
    return () => this.activityListeners.delete(listener);
  }

  nextGeneration(scope: string): number {
    const next = (this.generations.get(scope) ?? 0) + 1;
    this.generations.set(scope, next);
    return next;
  }

  isCurrent(scope: string, generation: number): boolean {
    return this.generations.get(scope) === generation;
  }

  async latestJson<T>(scope: string, url: string, options: RequestOptions = {}): Promise<T> {
    const generation = this.nextGeneration(scope);
    this.scopeControllers.get(scope)?.abort();
    const controller = new AbortController();
    this.scopeControllers.set(scope, controller);
    const forwardAbort = () => controller.abort();
    if (options.signal?.aborted) forwardAbort();
    else options.signal?.addEventListener('abort', forwardAbort, { once: true });
    try {
      const result = await this.json<T>(url, { ...options, signal: controller.signal });
      if (!this.isCurrent(scope, generation)) throw new StaleResponseError();
      return result;
    } finally {
      options.signal?.removeEventListener('abort', forwardAbort);
      if (this.scopeControllers.get(scope) === controller) this.scopeControllers.delete(scope);
    }
  }

  json<T>(url: string, options: RequestOptions = {}): Promise<T> {
    const key = canonicalRequestUrl(url);
    if (options.signal?.aborted) return Promise.reject(new DOMException('The request was aborted', 'AbortError'));
    if (options.reuseCompleted) {
      const retained = this.retained.get(key);
      if (retained) {
        this.retained.delete(key);
        this.retained.set(key, retained);
        this._stats.reused += 1;
        return Promise.resolve(retained.value as T);
      }
    }
    let task = this.inflight.get(key) as PendingTask<T> | undefined;
    if (task?.controller.signal.aborted) {
      if (this.inflight.get(key) === task) this.inflight.delete(key);
      task = undefined;
    }
    if (!task) {
      task = {
        key,
        url: key,
        priority: options.priority === 'offscreen' ? 1 : 0,
        controller: new AbortController(),
        consumers: new Set(),
        started: false,
        retry503: options.retry503 !== false,
        reuseCompleted: options.reuseCompleted === true
      };
      this.inflight.set(key, task);
      this.queued.push(task);
      this.queued.sort((a, b) => a.priority - b.priority);
    } else {
      this._stats.coalesced += 1;
      task.reuseCompleted ||= options.reuseCompleted === true;
      if (options.priority !== 'offscreen' && task.priority > 0 && !task.started) {
        task.priority = 0;
        this.queued.sort((a, b) => a.priority - b.priority);
      }
    }

    const promise = new Promise<T>((resolve, reject) => {
      const consumer: Consumer<T> = { resolve, reject, signal: options.signal };
      const abort = () => {
        task?.consumers.delete(consumer);
        this._stats.abandoned += 1;
        reject(new DOMException('The request was aborted', 'AbortError'));
        if (task && task.consumers.size === 0) {
          if (task.started) task.controller.abort();
          else this.removeQueued(task);
        }
      };
      consumer.abort = abort;
      if (options.signal?.aborted) abort();
      else {
        task?.consumers.add(consumer);
        options.signal?.addEventListener('abort', abort, { once: true });
      }
    });
    this.pump();
    return promise;
  }

  private removeQueued(task: PendingTask): void {
    const index = this.queued.indexOf(task);
    if (index >= 0) this.queued.splice(index, 1);
    if (this.inflight.get(task.key) === task) this.inflight.delete(task.key);
    this.publishActivity();
  }

  private pump(): void {
    while (this.active < this.concurrency && this.queued.length > 0) {
      const task = this.queued.shift();
      if (!task || task.consumers.size === 0) continue;
      task.started = true;
      this.active += 1;
      void this.execute(task).finally(() => {
        this.active -= 1;
        if (this.inflight.get(task.key) === task) this.inflight.delete(task.key);
        this._stats.completed += 1;
        this.pump();
      });
    }
    this.publishActivity();
  }

  private publishActivity(): void {
    const pendingRequests = this.active + this.queued.length;
    if (pendingRequests === this.publishedPendingRequests) return;
    this.publishedPendingRequests = pendingRequests;
    for (const listener of this.activityListeners) listener(pendingRequests);
  }

  private async execute<T>(task: PendingTask<T>): Promise<void> {
    try {
      const result = await this.fetchJson<T>(task.url, task.controller.signal, task.retry503);
      if (task.reuseCompleted) this.retain(task.key, result);
      for (const consumer of task.consumers) this.settleConsumer(consumer, () => consumer.resolve(result));
    } catch (error) {
      for (const consumer of task.consumers) this.settleConsumer(consumer, () => consumer.reject(error));
    } finally {
      task.consumers.clear();
    }
  }

  private retain(key: string, value: unknown): void {
    if (this.retainedLimitBytes <= 0) return;
    const bytes = new TextEncoder().encode(JSON.stringify(value)).byteLength;
    if (bytes > this.retainedLimitBytes) return;
    const previous = this.retained.get(key);
    if (previous) {
      this.retainedBytes -= previous.bytes;
      this.retained.delete(key);
    }
    while (this.retainedBytes + bytes > this.retainedLimitBytes && this.retained.size > 0) {
      const oldestKey = this.retained.keys().next().value as string;
      const oldest = this.retained.get(oldestKey);
      if (oldest) this.retainedBytes -= oldest.bytes;
      this.retained.delete(oldestKey);
    }
    this.retained.set(key, { value, bytes });
    this.retainedBytes += bytes;
  }

  private settleConsumer<T>(consumer: Consumer<T>, settle: () => void): void {
    consumer.signal?.removeEventListener('abort', consumer.abort as EventListener);
    if (!consumer.signal?.aborted) settle();
  }

  private async fetchJson<T>(url: string, signal: AbortSignal, retry503: boolean): Promise<T> {
    let attempt = 0;
    while (true) {
      // Firefox validates the Web IDL receiver for window.fetch. Calling a
      // stored fetch function as `this.fetcher(...)` incorrectly supplies the
      // RequestManager as that receiver, so invoke it with the browser global.
      const response = await this.fetcher.call(globalThis, url, {
        signal,
        headers: { Accept: 'application/json' }
      });
      const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
      if (response.status === 503 && retry503 && attempt === 0 && !signal.aborted) {
        attempt += 1;
        this._stats.retried += 1;
        await abortableDelay((retryAfter ?? 0.1) * 1000, signal);
        continue;
      }
      if (!response.ok) throw new HttpError(response.status, `Request failed with HTTP ${response.status}`, retryAfter);
      return response.json() as Promise<T>;
    }
  }
}

export function parseRetryAfter(value: string | null): number | null {
  if (!value) return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds;
  const date = Date.parse(value);
  if (!Number.isFinite(date)) return null;
  return Math.max(0, (date - Date.now()) / 1000);
}

function abortableDelay(milliseconds: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(resolve, milliseconds);
    signal.addEventListener('abort', () => {
      window.clearTimeout(timeout);
      reject(new DOMException('The request was aborted', 'AbortError'));
    }, { once: true });
  });
}

interface RmsWaiter {
  columns: string[];
  resolve(value: ApiEnvelope): void;
  reject(reason: unknown): void;
  signal?: AbortSignal;
}

interface RmsGroup {
  base: string;
  waiters: RmsWaiter[];
  scheduled: boolean;
}

/** Combines same-monitor/same-range RMS columns in one microtask, respecting the backend's eight-column boundary. */
export class RmsBatcher {
  private readonly groups = new Map<string, RmsGroup>();

  constructor(private readonly manager: RequestManager) {}

  request(base: string, columns: string[], signal?: AbortSignal): Promise<ApiEnvelope> {
    if (columns.length === 0 || columns.length > 8) throw new RangeError('RMS requests require between one and eight columns');
    const normalizedBase = base.replace(/\/$/, '');
    let group = this.groups.get(normalizedBase);
    if (!group) {
      group = { base: normalizedBase, waiters: [], scheduled: false };
      this.groups.set(normalizedBase, group);
    }
    const promise = new Promise<ApiEnvelope>((resolve, reject) => group?.waiters.push({ columns: [...new Set(columns)], resolve, reject, signal }));
    if (!group.scheduled) {
      group.scheduled = true;
      queueMicrotask(() => void this.flush(group as RmsGroup));
    }
    return promise;
  }

  private async flush(group: RmsGroup): Promise<void> {
    this.groups.delete(group.base);
    const live = group.waiters.filter((waiter) => !waiter.signal?.aborted);
    const pending = [...live];
    while (pending.length > 0) {
      const selected: RmsWaiter[] = [];
      const columns: string[] = [];
      for (const waiter of [...pending]) {
        const merged = [...new Set([...columns, ...waiter.columns])];
        if (merged.length <= 8) {
          selected.push(waiter);
          columns.splice(0, columns.length, ...merged);
          pending.splice(pending.indexOf(waiter), 1);
        }
      }
      try {
        const batchController = new AbortController();
        const abortIfUnused = () => {
          if (selected.every((waiter) => waiter.signal?.aborted)) batchController.abort();
        };
        for (const waiter of selected) waiter.signal?.addEventListener('abort', abortIfUnused);
        abortIfUnused();
        let response: ApiEnvelope;
        try {
          response = await this.manager.json<ApiEnvelope>(`${group.base}/${columns.map(encodeURIComponent).join('/')}`, { priority: 'visible', signal: batchController.signal });
        } finally {
          for (const waiter of selected) waiter.signal?.removeEventListener('abort', abortIfUnused);
        }
        for (const waiter of selected) {
          if (waiter.signal?.aborted) continue;
          waiter.resolve(projectRmsResponse(response, waiter.columns));
        }
      } catch (error) {
        for (const waiter of selected) if (!waiter.signal?.aborted) waiter.reject(error);
      }
    }
    for (const waiter of group.waiters.filter((item) => item.signal?.aborted)) waiter.reject(new DOMException('The request was aborted', 'AbortError'));
  }
}

export function projectRmsResponse(response: ApiEnvelope, columns: string[]): ApiEnvelope {
  if (!Array.isArray(response.columns) || !Array.isArray(response.data)) return response;
  const indexes = [0, ...columns.map((column) => response.columns?.indexOf(column) ?? -1)];
  if (indexes.some((index) => index < 0)) throw new Error('Batched RMS response omitted a requested column');
  return {
    ...response,
    columns: ['date', ...columns],
    data: (response.data as unknown[][]).map((row) => indexes.map((index) => row[index]))
  };
}

export const requestManager = new RequestManager();
export const rmsBatcher = new RmsBatcher(requestManager);
